import type { UseQueryResult } from '@tanstack/react-query';
import type { AxiosError } from 'axios';
import React, { createContext, useContext, useEffect, useState } from 'react';
import type {
	OsobyFotoDto,
	OsobyFyzickaOsobaDto,
	ResponseError,
} from '@gov-nx/api';
import {
	filterErrorStatus,
	robPersonPhotoQuery,
	robPersonQuery,
	usePoQueries,
	useAxiosInstance,
} from '@gov-nx/api';
import { isResponseStatus } from '@gov-nx/api';
import { GovError } from '@gov-nx/core/app';
import { useProcessControl } from '@gov-nx/core/hooks';
import type { Nullable } from '@gov-nx/core/types';
import { is } from '@gov-nx/core/types';
import type {
	ProfileROBContext,
	ProfileRobProcessControl,
} from './context.types';

const ProfilROBContext = createContext<Nullable<ProfileROBContext>>(null);

interface ProfilROBContextProviderProps {
	children: React.ReactNode;
}

export function ProfilROBContextProvider({
	children,
}: ProfilROBContextProviderProps) {
	const { setControls, controls } = useProcessControl<ProfileRobProcessControl>(
		{
			initialDataError: null,
			initialPhotoError: null,
			displayPhoto: false,
			displayComplaint: false,
		}
	);
	const [data, setData] = useState<Nullable<OsobyFyzickaOsobaDto>>(null);
	const [photo, setPhoto] = useState<Nullable<OsobyFotoDto['data']>>(null);
	const { axiosInstance } = useAxiosInstance();

	const mainQueries = usePoQueries<
		[
			UseQueryResult<OsobyFyzickaOsobaDto, AxiosError<ResponseError>>,
			UseQueryResult<OsobyFotoDto, AxiosError<ResponseError>>,
		]
	>({
		queries: [
			{
				queryKey: ['profile-rob'],
				queryFn: async () => robPersonQuery(axiosInstance),
				onError: (error: Error) => {
					setControls({ initialDataError: new GovError(error.message) });
				},
				retry: 0,
				refetchOnWindowFocus: false,
			},
			{
				queryKey: ['profile-rob-query'],
				queryFn: async () => robPersonPhotoQuery(axiosInstance),
				onError: (error: Error) => {
					setControls({ initialPhotoError: new GovError(error.message) });
				},
				retry: 0,
				refetchOnWindowFocus: false,
			},
		],
		configs: [
			{
				errorIgnoreFilter: filterErrorStatus(500, (initialError) =>
					setControls({ initialError })
				),
			},
			{
				errorIgnoreFilter: isResponseStatus(404),
			},
		],
	});
	const queriesResponses = mainQueries
		.map((result) => {
			if (result.isError || result.isFetched) {
				return result.data ?? false;
			}
			return undefined;
		})
		.filter(is);
	const isQueriesFetched = queriesResponses.length === mainQueries.length;

	useEffect(() => {
		setControls({ initialLoading: true });
		// eslint-disable-next-line react-hooks/exhaustive-deps -- These dependencies are correct
	}, []);

	useEffect(() => {
		if (isQueriesFetched && controls.initialLoading) {
			const robData = queriesResponses[0]
				? (queriesResponses[0] as OsobyFyzickaOsobaDto)
				: null;
			const robPhoto = queriesResponses[1]
				? (queriesResponses[1] as OsobyFotoDto)
				: null;

			setData(robData);

			setPhoto(robPhoto ? robPhoto.data : null);
			setControls({ initialLoading: false });
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps -- These dependencies are correct
	}, [isQueriesFetched]);

	return (
		<ProfilROBContext.Provider
			value={{
				controls,
				setControls,
				data,
				photo,
			}}>
			{children}
		</ProfilROBContext.Provider>
	);
}

export const useProfilROBContextInstance = (): ProfileROBContext =>
	useContext(ProfilROBContext) as ProfileROBContext;
